import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  Stack,
  Paper,
  IconButton,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as EmojiEventsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Project import
import { gridSpacing } from 'config.js';

// Styled Components
const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(color || theme.palette.primary.main, 0.08)} 0%, ${alpha(color || theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(color || theme.palette.primary.main, 0.3)
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
  }
}));

const ViewPenilaianKinerja = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [penilaianData, setPenilaianData] = useState(null);

  // Static data
  const mockData = {
    id: parseInt(id) || 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      department: 'IT & Web Master',
      employeeId: 'EMP001',
      email: 'ahmad.rizki@eurekagroup.id',
      phone: '+62 812-3456-7890'
    },
    period: 'Q1 2024',
    evaluator: 'Budi Manager',
    evaluatorPosition: 'IT Manager',
    evaluationDate: '2024-03-31',
    overallScore: 4.5,
    status: 'Completed',
    categories: {
      'Kualitas Kerja': { score: 4.5, description: 'Hasil kerja sangat baik dan konsisten' },
      'Produktivitas': { score: 4.8, description: 'Sangat produktif, selalu menyelesaikan tugas tepat waktu' },
      'Komunikasi': { score: 4.2, description: 'Komunikasi efektif dengan tim dan atasan' },
      'Kerjasama Tim': { score: 4.6, description: 'Bekerja sama dengan baik dalam tim' },
      'Inisiatif': { score: 4.3, description: 'Memiliki inisiatif yang baik dalam menyelesaikan masalah' }
    },
    strengths: [
      'Kemampuan teknis yang sangat baik',
      'Komitmen tinggi terhadap deadline',
      'Kemampuan problem solving yang baik'
    ],
    improvements: [
      'Dapat meningkatkan komunikasi dengan tim lintas departemen',
      'Dapat mengambil lebih banyak inisiatif untuk proyek baru'
    ],
    recommendations: 'Karyawan menunjukkan performa yang sangat baik. Disarankan untuk diberikan proyek yang lebih menantang dan peluang pengembangan karir.'
  };

  useEffect(() => {
    setTimeout(() => {
      setPenilaianData(mockData);
      setLoading(false);
    }, 500);
  }, [id]);

  const getScoreColor = (score) => {
    if (score >= 4.5) return theme.palette.success.main;
    if (score >= 3.5) return theme.palette.info.main;
    if (score >= 2.5) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'draft':
        return theme.palette.info.main;
      case 'cancelled':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!penilaianData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Data penilaian tidak ditemukan</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardHeader
          avatar={
            <IconButton onClick={() => navigate('/penilaian-kinerja')} sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          }
          title={
            <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Detail Penilaian Kinerja
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/penilaian-kinerja/edit/${id}`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Print
              </Button>
            </Stack>
          }
        />
      </Card>

      <Grid container spacing={3}>
        {/* Left Column - Employee Info & Overall Score */}
        <Grid item xs={12} lg={4}>
          {/* Employee Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700
                  }}
                >
                  {penilaianData.employee.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {penilaianData.employee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {penilaianData.employee.position}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {penilaianData.employee.department}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{penilaianData.employee.employeeId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{penilaianData.period}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    Evaluator: {penilaianData.evaluator}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          {/* Overall Score Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                Skor Keseluruhan
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: alpha(getScoreColor(penilaianData.overallScore), 0.1),
                  border: `4px solid ${getScoreColor(penilaianData.overallScore)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <Stack alignItems="center">
                    <StarIcon sx={{ color: getScoreColor(penilaianData.overallScore), fontSize: 32, mb: 0.5 }} />
                    <Typography variant="h3" sx={{ fontWeight: 700, color: getScoreColor(penilaianData.overallScore) }}>
                      {penilaianData.overallScore.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 5.0
                    </Typography>
                  </Stack>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(penilaianData.overallScore / 5) * 100}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: alpha(getScoreColor(penilaianData.overallScore), 0.1),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getScoreColor(penilaianData.overallScore),
                      borderRadius: 5
                    }
                  }}
                />
              </Box>
            </CardContent>
          </InfoCard>

          {/* Status Card */}
          <InfoCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Status Evaluasi
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={penilaianData.status}
                  sx={{
                    bgcolor: alpha(getStatusColor(penilaianData.status), 0.1),
                    color: getStatusColor(penilaianData.status),
                    border: `2px solid ${getStatusColor(penilaianData.status)}`,
                    fontSize: '1rem',
                    fontWeight: 700,
                    height: 48,
                    px: 3,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                  icon={penilaianData.status === 'Completed' ? <CheckCircleIcon /> : <PendingIcon />}
                />
                {penilaianData.evaluationDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Evaluasi: {new Date(penilaianData.evaluationDate).toLocaleDateString('id-ID')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Right Column - Detailed Scores */}
        <Grid item xs={12} lg={8}>
          {/* Category Scores */}
          <InfoCard sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon sx={{ color: 'primary.main' }} />
                  Penilaian Detail
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                {Object.entries(penilaianData.categories).map(([category, data]) => (
                  <Grid item xs={12} sm={6} key={category}>
                    <StatCard color={getScoreColor(data.score)}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {category}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <StarIcon sx={{ color: getScoreColor(data.score), fontSize: 20 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, color: getScoreColor(data.score) }}>
                              {data.score.toFixed(1)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              / 5.0
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={(data.score / 5) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(getScoreColor(data.score), 0.1),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getScoreColor(data.score),
                                borderRadius: 3
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {data.description}
                        </Typography>
                      </Stack>
                    </StatCard>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </InfoCard>

          {/* Strengths & Improvements */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmojiEventsIcon sx={{ color: 'success.main' }} />
                      Kekuatan
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={1.5}>
                    {penilaianData.strengths.map((strength, index) => (
                      <Box key={index} sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        borderLeft: `3px solid ${theme.palette.success.main}`
                      }}>
                        <Typography variant="body2">{strength}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ color: 'warning.main' }} />
                      Area Perbaikan
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={1.5}>
                    {penilaianData.improvements.map((improvement, index) => (
                      <Box key={index} sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        borderLeft: `3px solid ${theme.palette.warning.main}`
                      }}>
                        <Typography variant="body2">{improvement}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </InfoCard>
            </Grid>

            {/* Recommendations */}
            <Grid item xs={12}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Rekomendasi
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    {penilaianData.recommendations}
                  </Typography>
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewPenilaianKinerja;




